<template>
    <div class="qty-wrap" :class="cssClasses" :id="'qty-wrap' + sizeTitle">
        <div class="controls-container controls-container--decriment" @click="decriment()">
            <span class="minus-qty"> - </span>
        </div>
        <input
            class="size-row__input"
            :ref="'bundle-single-product-' + prId"
            :id="prId"
            :name="'size-qty-' + sizeTitle"
            maxlength="12"
            :value="value"
            @change="onChange($event.target.value, 'bundle-single-product-' + prId)"
            @keydown.enter="onEnter($event)"
            @keydown.up="onArrowUp($event)"
            @keydown.down="onArrowDown($event)"
            @click="$event.target.select()"
            type="number"
        >

        <div class="controls-container controls-container--incriment" @click="increment()">
            <span class="plus-qty"> + </span>
        </div>
        <label class="custom-qty-label" v-if="isTitleDisplay()" :for="'qty-wrap' + sizeTitle">
<!--            <span class="label-text">{{ sizeTitle }}</span>-->
            <span class="sub_upcharge"> {{ getSurcharge }}</span>
        </label>
    </div>
</template>

<script>
import { useSelectionStore } from '@/features/imprint/stores/useSelectionStore'
import { useImprintStore } from '@/features/imprint/stores/useImprintStore'

// Module-level counter: guarantees unique fallback IDs even when
// many CustomQty instances are created in the same millisecond (e.g. on resize)
let _qtyIdCounter = 0

export default {
    props: {
        // New props for new architecture
        line: {
            type: Object,
            required: false
        },
        imprintKey: {
            type: String,
            default: 'default-imprint'
        },
        
        // Old props for compatibility
        sizeTitle: String,
        modelValue: {
            type: [String, Number],
            required: false,
            default: 0
        },
        lineId: Number,
        cssClasses: {
            type: String,
            required: false
        },
        displayTitle: { 
          type: Boolean,
          required: false
        },
        prId: {
          type: String,
          required: false,
          // Fallback: use module-level counter to guarantee uniqueness
          // (Date.now() is NOT safe — multiple instances created in same ms get same value)
          default: () => `qty-${++_qtyIdCounter}`
        },
        minOrderQty: {
          type: Number,
          default: 0
        },
        qtyIncrement: {
          type: Number,
          default: 1
        }
    },

    data() {
        return {
            selectionStore: useSelectionStore(),
            imprintStore: useImprintStore(),
            qtyVal: 0,
            qtyError: null,
            maxQty: 10000,
            minQty: 0,
            isInitialized: false // Track if component has been initialized
        }
    },

    computed: {
        // Check if this is a qty-only product (no sizes)
        isQtyOnly() {
            return this.sizeTitle === 'qty'
        },

        // Get qty_increment from fullJson (works for ALL products, not just qty-only)
        effectiveQtyIncrement() {
            const currentSku = this.imprintStore?.selectedImprintMethod?.sku
            const qtyIncrementFromJson = this.imprintStore?.fullJson?.configurable_array?.[currentSku]?.qty_increment
            
            // First try to get from fullJson (most reliable source)
            if (qtyIncrementFromJson) {
                const value = Number(qtyIncrementFromJson)
                console.log('[CustomQty] effectiveQtyIncrement from fullJson:', value, 'sku:', currentSku)
                return value
            }
            
            // Fallback to prop (if passed from parent)
            if (this.qtyIncrement && this.qtyIncrement !== 1) {
                console.log('[CustomQty] effectiveQtyIncrement from prop:', this.qtyIncrement)
                return Number(this.qtyIncrement)
            }
            
            // Default fallback
            console.log('[CustomQty] effectiveQtyIncrement using default: 1')
            return 1
        },

        // Get min_order_qty from fullJson for qty-only products  
        effectiveMinOrderQty() {
            if (this.isQtyOnly) {
                const currentSku = this.imprintStore?.selectedImprintMethod?.sku
                const minOrderQtyFromJson = this.imprintStore?.fullJson?.configurable_array?.[currentSku]?.min_order_qty
                
                if (minOrderQtyFromJson) {
                    console.log('[CustomQty] Using min_order_qty from fullJson:', minOrderQtyFromJson)
                    return Number(minOrderQtyFromJson)
                }
            }
            
            // Fallback to prop
            return Number(this.minOrderQty) || 0
        },

        // Get product type
        productType() {
            return this.imprintStore?.fullJson?.product_type || 'grouped_plain'
        },

        applicationType() {
            return this.imprintStore?.fullJson?.application_type || 'grouped_plain'
        },

        // Get surcharge data
        getSurcharge() {
            const sizeUpcharge = this.imprintStore?.fullJson?.size_upcharge || {}
            if (sizeUpcharge[this.sizeTitle]) {

                console.log('surcharge');
                console.log(`${sizeUpcharge[this.sizeTitle].toFixed(2)}`);
                console.log(sizeUpcharge[this.sizeTitle].toFixed(2));
                return '+' . sizeUpcharge[this.sizeTitle].toFixed(2)
            }
            return ''
        },

        // Computed value getter/setter
        value: {
            get() {
                // Commented out: Logic that sets minimum quantity for grouped_plain (isQtyOnly)
                // if (this.productType === 'grouped_plain') {
                //     this.setDefaultQtyForLine()
                // }

                // New architecture - through line
                if (this.line && this.line.selectedSizes) {
                    const currentValue = this.line.selectedSizes[this.sizeTitle]
                    
                    // Check if value exists in store (undefined or null means not set yet)
                    if (currentValue !== undefined && currentValue !== 0) {
                        // Return actual value including 0
                        return currentValue < 0 ? 0 : currentValue
                    }
                    
                    // Commented out: First time opening - use default value
                    // if (!this.isInitialized && this.isQtyOnly) {
                    //     return this.effectiveMinOrderQty
                    // }
                    
                    return this.minQty
                }

                // Old architecture - through store (fallback)
                const lines = this.selectionStore.getLinesByImprintKey(this.imprintKey)
                if (lines && lines[this.lineId]) {
                    const currentValue = lines[this.lineId].selectedSizes?.[this.sizeTitle]
                    
                    // Check if value exists in store
                    if (currentValue !== undefined && currentValue !== null) {
                        // Return actual value including 0
                        return currentValue < 0 ? 0 : currentValue
                    }
                    
                    // Commented out: First time opening - use default value
                    // if (!this.isInitialized && this.isQtyOnly) {
                    //     return this.effectiveMinOrderQty
                    // }
                    
                    return this.minQty
                }

                // Commented out: Default value for qty-only
                // if (this.isQtyOnly) {
                //     return this.effectiveMinOrderQty
                // }

                return this.minQty
            },
            
            set(newValue) {
                const value = newValue < 0 ? 0 : newValue

                // Mark as initialized after first change
                this.isInitialized = true

                // Update through store
                if (this.line) {
                    this.selectionStore.updateLineSizeQty(
                        this.imprintKey,
                        this.line.id,
                        this.sizeTitle,
                        value
                    )
                }
            }
        }
    },

    methods: {
        isTitleDisplay() {
            if (this.displayTitle === true) {
                return true
            }

            if (this.productType === 'grouped_apparel' && this.applicationType === 'default') {
                return true
            }

            if (this.productType === 'grouped_plain') {
                return false
            }

            return false
        },

        setDefaultQtyForLine() {
            // Commented out: Logic that sets minOrderQty for grouped_plain (isQtyOnly)
            // if (this.productType === 'grouped_plain') {
            //     if (this.minOrderQty && this.value === 0) {
            //         this.value = this.minOrderQty
            //         return true
            //     }
            // }
            return false
        },

        increment() {
            let newValue = Number(this.value)
            const increment = this.effectiveQtyIncrement
            
            console.log('[CustomQty] increment() called:', {
                currentValue: newValue,
                effectiveQtyIncrement: increment,
                qtyIncrementProp: this.qtyIncrement,
                isQtyOnly: this.isQtyOnly,
                currentSku: this.imprintStore?.selectedImprintMethod?.sku,
                qtyIncrementFromJson: this.imprintStore?.fullJson?.configurable_array?.[this.imprintStore?.selectedImprintMethod?.sku]?.qty_increment
            })

            if (newValue < this.maxQty) {
                if (this.qtyError) {
                    const remainder = newValue % increment
                    newValue = newValue + increment - remainder
                    this.qtyError = null
                } else {
                    newValue = newValue + increment
                }
            } else {
                newValue = Number(this.maxQty)
            }

            this.value = newValue
            this.emitChange(newValue)
        },

        decriment() {
            if (this.minQty < 0) {
                this.minQty = 0
            }

            let newValue = Number(this.value)
            const increment = this.effectiveQtyIncrement

            // Handle error state
            if (this.qtyError) {
                const remainder = newValue % increment
                newValue = newValue - remainder
                this.qtyError = null
            } else {
                newValue = newValue - increment
            }

            // Ensure value doesn't go below 0
            if (newValue < 0) {
                newValue = 0
            }

            this.value = newValue
            this.emitChange(newValue)
        },

        onChange(inputValue, refValue = null) {
            if (this.minQty < 0) {
                this.minQty = 0
            }

            let value = Number(inputValue)
            const increment = this.effectiveQtyIncrement

            // Handle negative values
            if (value < 0) {
                value = 0
            }

            // Handle max value
            if (value > this.maxQty) {
                value = Number(this.maxQty)
            }

            // Special logic for calendar
            if (this.applicationType === 'calendar' || this.applicationType === 'calendar_single') {
                let currValue = 25
                
                if (value < 25) {
                    currValue = 25
                } else if (value % increment) {
                    currValue = this.roundValueBy(value, increment)
                }

                if (value > 25 && !(value % increment)) {
                    currValue = value
                }

                this.value = currValue
                if (this.$refs[refValue]) {
                    this.$refs[refValue].value = currValue
                }
                this.emitChange(currValue)
            } else {
                // Round value to nearest increment if not 0
                if (value === 0) {
                    // Allow 0
                    this.value = 0
                    this.emitChange(0)
                    this.qtyError = null
                    this.emitError(null)
                } else if (value < increment) {
                    // Round up to minimum increment
                    value = increment
                    this.value = value
                    if (this.$refs[refValue]) {
                        this.$refs[refValue].value = value
                    }
                    this.emitChange(value)
                    this.qtyError = null
                    this.emitError(null)
                } else {
                    // Round to nearest increment
                    const remainder = value % increment
                    if (remainder !== 0) {
                        // Round to nearest (up or down)
                        if (remainder >= increment / 2) {
                            value = value + (increment - remainder)
                        } else {
                            value = value - remainder
                        }
                    }
                    
                    this.value = value
                    if (this.$refs[refValue]) {
                        this.$refs[refValue].value = value
                    }
                    this.emitChange(value)
                    this.qtyError = null
                    this.emitError(null)
                }
            }
        },

        onEnter(event) {
            event.preventDefault()
            const inputValue = event.target.value
            
            // Process and round the value just like increment/decrement does
            let value = Number(inputValue)
            const increment = this.effectiveQtyIncrement

            // Allow 0
            if (value < 0) {
                value = 0
            } else if (value === 0) {
                // Keep as 0
            } else if (value < increment) {
                // Round up to minimum increment
                value = increment
            } else if (value > this.maxQty) {
                value = this.maxQty
            } else {
                // Round to nearest increment
                const remainder = value % increment
                if (remainder !== 0) {
                    // Round to nearest (up or down)
                    if (remainder >= increment / 2) {
                        value = value + (increment - remainder)
                    } else {
                        value = value - remainder
                    }
                }
            }

            // Update value and emit
            this.value = value
            event.target.value = value
            this.qtyError = null
            this.emitChange(value)
            this.emitError(null)
            
            // Blur the input to hide keyboard on mobile
            event.target.blur()
        },

        onArrowUp(event) {
            event.preventDefault()
            // Same logic as increment button
            this.increment()
        },

        onArrowDown(event) {
            event.preventDefault()
            // Same logic as decrement button
            this.decriment()
        },

        roundValueBy(value, by) {
            return Math.round(parseInt(value) / parseInt(by)) * parseInt(by)
        },

        validateInputQty(value) {
            const increment = this.effectiveQtyIncrement
            
            // Skip validation for 0
            if (value === 0) {
                this.qtyError = null
                this.emitError(null)
                return true
            }
            
            const failCondition = value < increment || value % increment !== 0 || !Number.isInteger(value)

            if (failCondition) {
                this.qtyError = `Item purchasable in increments of ${increment}`
                this.emitError(this.qtyError)
                return false
            } else {
                this.qtyError = null
                this.emitError(null)
                return true
            }
        },

        emitChange(value) {
            // New event for ColorSizeLine
            this.$emit('update-quantity', {
                lineId: this.line?.id || this.lineId,
                size: this.sizeTitle,
                quantity: value
            })

            // Old event for compatibility
            this.$emit('customQtyChange', {
                value: value,
                lineIndex: this.lineId,
                title: this.sizeTitle
            })
        },

        emitError(error) {
            this.$emit('showQtyError', { qtyError: error })
        }
    },

    created() {
        // Special logic for calendar
        if (this.applicationType === 'calendar_single' || this.applicationType === 'calendar') {
            this.minQty = 25

            if (window.jQuery && window.jQuery("#calendar-qty-changed")) {
                window.jQuery("body").on("click", "#calendar-qty-changed", (event) => {
                    if (window.jQuery("#calendar-imprint-common-qty")) {
                        const qtyValue = window.jQuery("#calendar-imprint-common-qty").val()
                        this.value = qtyValue
                        this.emitChange(qtyValue)
                    }
                })
            }
        }
    },

    mounted() {
        // Check if line already has a value set
        if (this.line && this.line.selectedSizes) {
            const currentValue = this.line.selectedSizes[this.sizeTitle]
            if (currentValue !== undefined && currentValue !== null) {
                this.isInitialized = true
            }
        }
        
        // Don't emit change on mount - this would mark auto-initialized colors as user-modified
        // The value is already in the store, no need to emit it again
    }
}
</script>

<style scoped lang="scss">
@use '@/styles/components/custom-qty';
</style>
